import React from "react"
import { Link } from "react-router-dom"
import { Modal, ModalHeader, ModalBody, Button } from "reactstrap"
import type { PropertyKycBlockReason } from "helpers/auth_api"

export interface KycRequiredModalProps {
  isOpen: boolean
  toggle: () => void
  /** upload = missing KYC docs; pending = submitted, waiting for admin */
  variant?: PropertyKycBlockReason
  /** When set (e.g. on /profile), “Complete your KYC” closes this modal and runs this instead of navigating to /profile */
  onCompleteKyc?: () => void
}

/** Centered modal before property / bidding access (used from listing pages). */
const KycRequiredModal: React.FC<KycRequiredModalProps> = ({ isOpen, toggle, variant = "upload", onCompleteKyc }) => {
  const isPending = variant === "pending"

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      size="md"
      contentClassName="border-0 shadow-lg rounded-4 overflow-hidden"
    >
      <ModalHeader
        toggle={toggle}
        className="border-0 pb-0 pt-4 px-4 text-center flex-column align-items-center position-relative"
      >
        <div
          className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center mx-auto mb-3"
          style={{ width: 72, height: 72 }}
        >
          <i
            className={`mdi ${isPending ? "mdi-clock-outline" : "mdi-shield-alert"} text-primary`}
            style={{ fontSize: 40 }}
            aria-hidden
          />
        </div>
        <span className="h5 mb-0 fw-semibold text-body">
          {isPending ? "Please wait for KYC approval" : "Please complete your KYC first"}
        </span>
      </ModalHeader>
      <ModalBody className="px-4 pb-4 pt-2 text-center">
        <p className="text-muted mb-4 small lh-base">
          {isPending
            ? "Your KYC documents are submitted. An administrator will review and approve your account soon. You can check status updates on your profile."
            : "Upload your KYC from your profile. After an administrator approves your account, you can view properties, place bids, and use listings."}
        </p>
        <div className="d-flex flex-column gap-2">
          {!isPending &&
            (onCompleteKyc ? (
              <Button
                color="primary"
                className="py-2 fw-semibold"
                onClick={() => {
                  onCompleteKyc()
                }}
              >
                Complete your KYC
              </Button>
            ) : (
              <Button tag={Link} to="/profile?kycCapture=1" color="primary" className="py-2 fw-semibold" onClick={toggle}>
                Complete your KYC
              </Button>
            ))}
          {isPending && (
            <Button tag={Link} to="/profile" color="primary" className="py-2 fw-semibold" outline onClick={toggle}>
              View profile status
            </Button>
          )}
          <Button color="light" className="border py-2 text-muted" onClick={toggle}>
            Close
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default KycRequiredModal
